import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Button, Container, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { brand, heroSlides } from "../data/estoreContent";
import { ESTORE_ROUTES } from "../data/estorePaths";

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir < 0 ? "100%" : "-100%", opacity: 0 }),
};

function EstoreHero({ stickyOffset }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setDir(1);
      setIndex((i) => (i + 1) % heroSlides.length);
    }, 6500);
    return () => clearInterval(t);
  }, []);

  const prevSlide = () => {
    setDir(-1);
    setIndex((i) => (i - 1 + heroSlides.length) % heroSlides.length);
  };

  const nextSlide = () => {
    setDir(1);
    setIndex((i) => (i + 1) % heroSlides.length);
  };

  const current = heroSlides[index];

  return (
    <Box
      sx={{
        position: "relative",
        mt: stickyOffset ? "64px" : 0,
        height: { xs: 420, sm: 480, md: 560 },
        overflow: "hidden",
        borderBottom: `1px solid ${brand.stroke}`,
      }}
    >
      <AnimatePresence initial={false} custom={dir} mode="sync">
        <motion.div
          key={current.id}
          custom={dir}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
          style={{
            position: "absolute",
            inset: 0,
          }}
        >
          <Box
            component="img"
            src={current.src}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(105deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.35) 45%, rgba(15,23,42,0.2) 100%)",
            }}
          />
        </motion.div>
      </AnimatePresence>

      <Container
        maxWidth="lg"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <Box sx={{ pointerEvents: "auto", maxWidth: { xs: "100%", md: "58%" } }}>
          <motion.div
            key={current.id + "-text"}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            <Typography
              variant={isMobile ? "h4" : "h2"}
              sx={{
                color: "#fff",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                textShadow: "0 12px 40px rgba(0,0,0,0.35)",
              }}
            >
              {current.title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                color: "rgba(255,255,255,0.88)",
                fontWeight: 500,
                lineHeight: 1.5,
                maxWidth: 480,
              }}
            >
              {current.subtitle}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(ESTORE_ROUTES.shop)}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1.25,
                  fontWeight: 800,
                  textTransform: "none",
                  bgcolor: "#fff",
                  color: brand.text,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                }}
              >
                Shop the edit
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(ESTORE_ROUTES.account)}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: "none",
                  borderColor: "rgba(255,255,255,0.55)",
                  color: "#fff",
                  "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                }}
              >
                Your account
              </Button>
            </Stack>
          </motion.div>
        </Box>
      </Container>

      <IconButton
        onClick={prevSlide}
        sx={{
          position: "absolute",
          left: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          bgcolor: "rgba(255,255,255,0.12)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
        }}
        aria-label="Previous slide"
      >
        <ChevronLeft />
      </IconButton>
      <IconButton
        onClick={nextSlide}
        sx={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          bgcolor: "rgba(255,255,255,0.12)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
        }}
        aria-label="Next slide"
      >
        <ChevronRight />
      </IconButton>

      <Stack
        direction="row"
        spacing={1}
        sx={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)" }}
      >
        {heroSlides.map((s, i) => (
          <Box
            key={s.id}
            component="button"
            type="button"
            onClick={() => {
              setDir(i > index ? 1 : -1);
              setIndex(i);
            }}
            sx={{
              width: i === index ? 28 : 8,
              height: 8,
              borderRadius: 999,
              border: "none",
              p: 0,
              cursor: "pointer",
              bgcolor: i === index ? "#fff" : "rgba(255,255,255,0.35)",
              transition: "width 0.35s ease, background 0.25s",
            }}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default EstoreHero;
